// src/pages/Domiciliarios.js
//
// Gestión de domiciliarios del POS Compacto.
//
// Roles de domiciliario en el ecosistema:
//   - POS_ONLY: creado desde el POS Compacto por el admin. No tiene
//     login, no ve el POS ni HolaVeci. Solo lo administra el tendero
//     para asignarle pedidos telefónicos.
//   - HOLAVECI: participa en el ecosistema HolaVeci. Tiene AspNetUser
//     y usa la app HolaVeci Domiciliario para recibir pedidos
//     automáticos. Se crea desde el flujo de registro de esa app.
//
// Esta pantalla permite crear/listar los POS_ONLY. Los HOLAVECI se
// muestran también en la lista con badge distinto pero no se pueden
// editar desde aquí.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, Typography, Stack, Chip, Avatar, Button,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  TextField, MenuItem, InputAdornment, LinearProgress, Alert, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab, Tooltip,
} from '@mui/material';
import SearchIcon        from '@mui/icons-material/Search';
import RefreshIcon       from '@mui/icons-material/Refresh';
import AddIcon           from '@mui/icons-material/Add';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import EditIcon          from '@mui/icons-material/Edit';
import { useAuth } from '../context/AuthContext';
import { useApp }  from '../context/AppContext';
import { domiciliarios as apiDom } from '../api/posApi';

const Domiciliarios = () => {
  const { user }   = useAuth();
  const { empresa, sucursales, notify } = useApp();
  const empresaId = empresa?.empresaId || empresa?.EmpresaId;

  const [datos, setDatos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [tick, setTick] = useState(0);
  const [tab, setTab] = useState(0);  // 0=POS_ONLY, 1=HOLAVECI, 2=Todos
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(null);

  const tipoFiltro = ['POS_ONLY', 'HOLAVECI', ''][tab];

  const cargar = useCallback(async () => {
    if (!empresaId) return;
    setCargando(true);
    try {
      const filtros = {};
      if (tipoFiltro) filtros.tipoAcceso = tipoFiltro;
      if (search.trim()) filtros.buscar = search.trim();
      const r = await apiDom.listar(empresaId, filtros);
      setDatos(Array.isArray(r) ? r : []);
    } catch (e) {
      notify(e.mensajeUsuario || 'No se pudieron cargar los domiciliarios.', 'error');
    } finally { setCargando(false); }
  }, [empresaId, tipoFiltro, search, notify]);

  useEffect(() => { cargar(); }, [cargar, tick]);

  const cambiarEstado = async (dom, nuevoEstado) => {
    try {
      await apiDom.cambiarEstado(dom.domiciliarioId, nuevoEstado);
      notify(`Estado actualizado a ${nuevoEstado}.`, 'success');
      setTick(t => t + 1);
    } catch (e) { notify(e.mensajeUsuario || 'No se pudo cambiar el estado.', 'error'); }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <LocalShippingIcon color="primary" fontSize="large" />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight={700}>Domiciliarios</Typography>
          <Typography variant="body2" color="text.secondary">
            Repartidores de tu empresa
          </Typography>
        </Box>
        <IconButton onClick={() => setTick(t => t + 1)}><RefreshIcon /></IconButton>
        <Button variant="contained" startIcon={<AddIcon />}
                onClick={() => setDialog({ modo: 'nuevo' })}>
          Nuevo domiciliario
        </Button>
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tab label="Del POS (sin login)" />
        <Tab label="HolaVeci (con app)" />
        <Tab label="Todos" />
      </Tabs>

      {tab === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Domiciliarios <b>POS_ONLY</b>: los administras desde aquí. No tienen
          login ni ven ninguna aplicación. Sirven para asignarles pedidos
          telefónicos y llevar registro de sus entregas.
        </Alert>
      )}
      {tab === 1 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Domiciliarios <b>HolaVeci</b>: se registraron por la app HolaVeci
          Domiciliario y participan en el ecosistema. Desde aquí puedes verlos
          y cambiar su estado, pero no editarlos.
        </Alert>
      )}

      <Card sx={{ mb: 2, p: 2 }}>
        <TextField fullWidth size="small"
                   placeholder="Buscar por nombre, documento o celular"
                   value={search} onChange={(e) => setSearch(e.target.value)}
                   InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
      </Card>

      <Card>
        {cargando && <LinearProgress />}
        {!cargando && datos.length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>
            Sin domiciliarios en esta vista.
            {tab === 0 && ' Crea el primero con el botón "Nuevo domiciliario".'}
          </Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Documento</TableCell>
                  <TableCell>Celular</TableCell>
                  <TableCell>Vehículo</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="center">Entregas</TableCell>
                  <TableCell align="right"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {datos.map(d => (
                  <TableRow key={d.domiciliarioId} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar src={d.fotoUrl}
                                sx={{ width: 34, height: 34, fontSize: 13 }}>
                          {(d.nombreCompleto || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
                        </Avatar>
                        <Typography variant="body2" fontWeight={500}>
                          {d.nombreCompleto || '(sin nombre)'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell><Typography variant="caption">{d.tipoDocumento} {d.numeroDocumento}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{d.celular || '—'}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {d.tipoVehiculo} {d.placaVehiculo ? `· ${d.placaVehiculo}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small"
                            label={d.tipoAcceso === 'POS_ONLY' ? 'Del POS' : 'HolaVeci'}
                            color={d.tipoAcceso === 'POS_ONLY' ? 'default' : 'primary'}
                            variant={d.tipoAcceso === 'POS_ONLY' ? 'outlined' : 'filled'} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small"
                            label={d.estado}
                            color={d.estado === 'ACTIVO' ? 'success'
                                 : d.estado === 'INACTIVO' ? 'default'
                                 : 'warning'} />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="caption">
                        {d.entregasExitosas}/{d.totalEntregas}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {d.tipoAcceso === 'POS_ONLY' && (
                        <Tooltip title="Editar">
                          <IconButton size="small"
                                      onClick={() => setDialog({ modo: 'editar', dom: d })}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {d.estado === 'INACTIVO' ? (
                        <Button size="small" onClick={() => cambiarEstado(d, 'ACTIVO')}>Activar</Button>
                      ) : (
                        <Button size="small" color="warning"
                                onClick={() => cambiarEstado(d, 'INACTIVO')}>Suspender</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {dialog && (
        <DialogDomiciliario
          empresaId={empresaId}
          sucursales={sucursales || []}
          modo={dialog.modo}
          dom={dialog.dom}
          onClose={() => setDialog(null)}
          onDone={() => { setDialog(null); setTick(t => t + 1);
                          notify(dialog.modo === 'nuevo' ? 'Domiciliario creado.' : 'Datos actualizados.', 'success'); }}
          onError={(m) => notify(m, 'error')} />
      )}
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════════
// Dialog crear/editar domiciliario
// ══════════════════════════════════════════════════════════════════════

function DialogDomiciliario({ empresaId, sucursales, modo, dom, onClose, onDone, onError }) {
  const [f, setF] = useState({
    numeroDocumento: dom?.numeroDocumento || '',
    tipoDocumento:   dom?.tipoDocumento || 'CC',
    primerNombre:    dom?.primerNombre || (dom?.nombreCompleto?.split(' ')[0] || ''),
    primerApellido:  dom?.primerApellido || (dom?.nombreCompleto?.split(' ').slice(-1)[0] || ''),
    celular:         dom?.celular || '',
    email:           dom?.email || '',
    tipoVehiculo:    dom?.tipoVehiculo || 'MOTO',
    placaVehiculo:   dom?.placaVehiculo || '',
    sucursalId:      dom?.sucursalId || (sucursales[0]?.sucursalId || ''),
    radioCobertura:  dom?.radioCobertura || 5,
  });
  const [g, setG] = useState(false);

  const submit = async () => {
    if (!f.primerNombre.trim() || !f.primerApellido.trim() || !f.numeroDocumento.trim() || !f.celular.trim()) {
      onError('Nombre, apellido, documento y celular son obligatorios.'); return;
    }
    setG(true);
    try {
      const dto = {
        empresaId,
        sucursalId: Number(f.sucursalId) || null,
        numeroDocumento: f.numeroDocumento.trim(),
        tipoDocumento: f.tipoDocumento,
        primerNombre: f.primerNombre.trim(),
        primerApellido: f.primerApellido.trim(),
        celular: f.celular.trim(),
        email: f.email.trim() || null,
        tipoVehiculo: f.tipoVehiculo,
        placaVehiculo: f.placaVehiculo.trim() || null,
        radioCobertura: Number(f.radioCobertura) || 5,
        tipoAcceso: 'POS_ONLY',
      };
      if (modo === 'nuevo') await apiDom.crear(dto);
      else await apiDom.actualizar(dom.domiciliarioId, dto);
      onDone();
    } catch (e) { onError(e.mensajeUsuario || 'No se pudo guardar.'); }
    finally { setG(false); }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {modo === 'nuevo' ? 'Nuevo domiciliario del POS' : 'Editar domiciliario'}
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Este domiciliario <b>no tendrá login</b>. Solo tú lo administrarás
          desde aquí para asignarle pedidos.
        </Alert>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField required fullWidth label="Primer nombre"
                       value={f.primerNombre}
                       onChange={(e) => setF(s => ({ ...s, primerNombre: e.target.value }))} />
            <TextField required fullWidth label="Primer apellido"
                       value={f.primerApellido}
                       onChange={(e) => setF(s => ({ ...s, primerApellido: e.target.value }))} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField select label="Tipo doc." sx={{ minWidth: 120 }}
                       value={f.tipoDocumento}
                       onChange={(e) => setF(s => ({ ...s, tipoDocumento: e.target.value }))}>
              <MenuItem value="CC">CC</MenuItem>
              <MenuItem value="CE">CE</MenuItem>
              <MenuItem value="TI">TI</MenuItem>
              <MenuItem value="PASAPORTE">Pasaporte</MenuItem>
            </TextField>
            <TextField required fullWidth label="Número de documento"
                       value={f.numeroDocumento}
                       onChange={(e) => setF(s => ({ ...s, numeroDocumento: e.target.value }))} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField required fullWidth label="Celular"
                       value={f.celular}
                       onChange={(e) => setF(s => ({ ...s, celular: e.target.value }))} />
            <TextField fullWidth label="Correo (opcional)"
                       value={f.email}
                       onChange={(e) => setF(s => ({ ...s, email: e.target.value }))} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField select label="Vehículo" sx={{ minWidth: 140 }}
                       value={f.tipoVehiculo}
                       onChange={(e) => setF(s => ({ ...s, tipoVehiculo: e.target.value }))}>
              <MenuItem value="MOTO">Moto</MenuItem>
              <MenuItem value="BICICLETA">Bicicleta</MenuItem>
              <MenuItem value="AUTO">Auto</MenuItem>
              <MenuItem value="A_PIE">A pie</MenuItem>
            </TextField>
            <TextField fullWidth label="Placa (opcional)"
                       value={f.placaVehiculo}
                       onChange={(e) => setF(s => ({ ...s, placaVehiculo: e.target.value.toUpperCase() }))} />
          </Stack>
          {sucursales.length > 1 && (
            <TextField select label="Sucursal asignada"
                       value={f.sucursalId}
                       onChange={(e) => setF(s => ({ ...s, sucursalId: e.target.value }))}>
              {sucursales.map(s => (
                <MenuItem key={s.sucursalId} value={s.sucursalId}>{s.nombre}</MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={g}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={g}>
          {g ? 'Guardando…' : (modo === 'nuevo' ? 'Crear domiciliario' : 'Guardar')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default Domiciliarios;
